import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  useBrokerStore,
  useAllPositions,
  useAllOrders,
  useConnectedBrokers,
  useActiveBrokerBalance,
  BrokerPosition,
  BrokerOrder,
} from '../../store/brokerStore';
import { useMarketStore } from '../../store/marketStore';
import { usePreferences } from '../../hooks/usePreferences';
import { useConnectionStatus } from '../../store/selectors';
import { PositionsSummary } from './PositionsSummary';
import { PositionsExpanded } from './PositionsExpanded';
import './ActionBarPositions.css';
import { normalizeToBase } from '../../utils/symbolUtils';

interface ActionBarPositionsProps {
  symbol?: string;
  currentPrice?: number;
  onOpenSettings?: () => void;
}

export const ActionBarPositions: React.FC<ActionBarPositionsProps> = ({
  symbol: propSymbol,
  currentPrice: propCurrentPrice,
  onOpenSettings,
}) => {
  // All hooks must be called unconditionally at the top
  const connectionStatus = useConnectionStatus();

  // Market data
  const marketSymbol = useMarketStore(state => state.symbol);
  const marketPrice = useMarketStore(state => state.currentPrice);

  // Broker data
  const brokers = useBrokerStore(state => state.brokers);
  const connectedBrokers = useConnectedBrokers();
  const allPositions = useAllPositions();
  const allOrders = useAllOrders();
  const activeBrokerBalance = useActiveBrokerBalance();

  // Actions
  const flattenPosition = useBrokerStore(state => state.flattenPosition);
  const cancelOrder = useBrokerStore(state => state.cancelOrder);
  const modifyOrder = useBrokerStore(state => state.modifyOrder);
  const fetchPositions = useBrokerStore(state => state.fetchPositions);
  const fetchOrders = useBrokerStore(state => state.fetchOrders);
  const placeOrder = useBrokerStore(state => state.placeOrder);

  // UI state
  const [isExpanded, setIsExpanded] = useState(false);
  const { preferences, updatePreference } = usePreferences();
  const isPinned = preferences?.actionBarPositions?.isPinned ?? false;

  // Refs for click-outside detection
  const containerRef = useRef<HTMLDivElement>(null);
  const expandedRef = useRef<HTMLDivElement>(null);

  // Calculate position for dropdown
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  // Derived values
  const symbol = propSymbol || marketSymbol;
  const currentPrice = propCurrentPrice ?? marketPrice;

  const currentBaseSymbol = symbol ? normalizeToBase(symbol) : '';
  const symbolPositions = allPositions.filter(p => p.symbol && normalizeToBase(p.symbol) === currentBaseSymbol);

  // Calculate working orders count
  const workingOrders = allOrders.filter(
    o => o.ordStatus === 'Working' || o.ordStatus === 'Accepted' || o.ordStatus === 'Pending'
  );

  // Toggle expand
  const handleToggleExpand = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  // Toggle pin
  const handleTogglePin = useCallback(() => {
    updatePreference('actionBarPositions', { isPinned: !isPinned });
  }, [isPinned, updatePreference]);

  // Close expanded view
  const handleClose = useCallback(() => {
    setIsExpanded(false);
  }, []);

  // Refresh all data
  const handleRefresh = useCallback(() => {
    connectedBrokers.forEach(broker => {
      fetchPositions(broker.id);
      fetchOrders(broker.id);
    });
  }, [connectedBrokers, fetchPositions, fetchOrders]);

  // Flatten position
  const handleFlatten = useCallback((position: BrokerPosition) => {
    if (!position.symbol) return;
    flattenPosition(position.symbol, position.brokerId, position.accountId);
  }, [flattenPosition]);

  // Flatten all positions
  const handleFlattenAll = useCallback(() => {
    allPositions
      .filter((p): p is BrokerPosition & { symbol: string } => !!p.symbol)
      .forEach(position => {
        flattenPosition(position.symbol, position.brokerId, position.accountId);
      });
  }, [allPositions, flattenPosition]);

  // Cancel order
  const handleCancelOrder = useCallback((order: BrokerOrder) => {
    cancelOrder(order.id, order.brokerId, order.accountId);
  }, [cancelOrder]);

  // Cancel all orders
  const handleCancelAllOrders = useCallback(() => {
    workingOrders.forEach(order => {
      cancelOrder(order.id, order.brokerId, order.accountId);
    });
  }, [workingOrders, cancelOrder]);

  // Modify order
  const handleModifyOrder = useCallback((
    orderId: number,
    newPrice: number,
    isStopPrice: boolean,
    brokerId?: string,
    accountId?: number,
    skipDebounce?: boolean
  ) => {
    modifyOrder(orderId, newPrice, isStopPrice, brokerId, accountId, skipDebounce);
  }, [modifyOrder]);

  // Partial exit - place a market order to close part of the position
  const handlePartialExit = useCallback((position: BrokerPosition, quantity: number) => {
    if (!position.symbol) return;
    const isLong = position.netPos > 0;
    placeOrder({
      action: isLong ? 'Sell' : 'Buy',
      symbol: position.symbol,
      quantity: quantity,
      brokerId: position.brokerId,
      accountId: position.accountId,
    });
  }, [placeOrder]);

  // Click outside to close (when not pinned)
  useEffect(() => {
    if (!isExpanded || isPinned) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Check if click is outside both the summary container and expanded panel
      const isOutsideContainer = containerRef.current && !containerRef.current.contains(target);
      const isOutsideExpanded = expandedRef.current && !expandedRef.current.contains(target);

      if (isOutsideContainer && isOutsideExpanded) {
        setIsExpanded(false);
      }
    };

    // Delay adding listener to prevent immediate close
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100); // Slightly longer delay to avoid race conditions

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExpanded, isPinned]);

  useEffect(() => {
    if (isExpanded && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: Math.max(8, rect.left - 200), // Offset to the left a bit since expanded is wider
      });
    }
  }, [isExpanded]);

  // Don't render when WebSocket is disconnected
  if (connectionStatus !== 'connected') {
    return null;
  }

  // Don't render if no connected brokers
  if (connectedBrokers.length === 0) {
    return null;
  }

  return (
    <div ref={containerRef} className="action-bar-positions">
      <PositionsSummary
        positions={symbolPositions}
        totalPnL={activeBrokerBalance.totalPnL}
        workingOrdersCount={workingOrders.length}
        connectedBrokersCount={connectedBrokers.length}
        isExpanded={isExpanded}
        onClick={handleToggleExpand}
        onRefresh={handleRefresh}
      />
      {isExpanded && createPortal(
        <div
          ref={expandedRef}
          className="abp-expanded-portal"
          style={{
            position: 'fixed',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            zIndex: 1000,
          }}
        >
          <PositionsExpanded
            brokers={brokers}
            positions={allPositions}
            orders={allOrders}
            totalBalance={activeBrokerBalance.netLiq}
            totalPnL={activeBrokerBalance.totalPnL}
            currentPrice={currentPrice}
            symbol={symbol}
            isPinned={isPinned}
            onTogglePin={handleTogglePin}
            onClose={handleClose}
            onRefresh={handleRefresh}
            onFlatten={handleFlatten}
            onFlattenAll={handleFlattenAll}
            onCancelOrder={handleCancelOrder}
            onCancelAllOrders={handleCancelAllOrders}
            onModifyOrder={handleModifyOrder}
            onPartialExit={handlePartialExit}
            onOpenSettings={onOpenSettings}
          />
        </div>,
        document.body
      )}
    </div>
  );
};

export default ActionBarPositions;
